/*
tslint:disable:import-spacing no-inferrable-types
variable-name align no-trailing-whitespace component-class-suffix
no-string-literal component-selector
*/

import 'ag-grid-enterprise';

import { first }                   from 'rxjs/operators';

import { Logger }                  from '@/util/logger';
import { UserLogService }          from '@/services/user.log.service';
import { UserLogNodeFactory }      from '@/models/userlog/user.log.node.factory';
import { Train }                   from '@/models/scenario/train';
import { DecisionPoint }           from '@/models/scenario/decision.point';
import {IServerSideDatasource, IServerSideGetRowsParams} from "ag-grid-community";

/**
 *
 * @class UserLogDataSource
 *
 * @brief adapter to asynchronously give our user log tree data to AG Grid for display
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      May 2021
 *
 */
export class UserLogDataSource implements IServerSideDatasource {

  /** the user logging component this is all tied to */
  private context: any             = null;

  /** the service we use to fetch children of nodes */
  private userLogs: UserLogService = null;

  /** the name of the user logging collection in MongoDB */
  private logContext: string       = '';

  /** Initial train focus (if there is one) */
  private train: Train             = null;

  /** initial decision point focus (if there is one) */
  private dp: DecisionPoint        = null;

  constructor(
    collection: string,
    ul:         UserLogService,
    context:    any,
    train:      Train           = null,
    dp:         DecisionPoint   = null
  ) {

    this.logContext = collection;
    this.context    = context;
    this.userLogs   = ul;
    this.train      = train;
    this.dp         = dp;
  }

  /** asynchronously fetch the requested tree data */
  getRows(params: IServerSideGetRowsParams): void {

    /* do we need the root? */

    const ulds: UserLogDataSource = this;

    let parentKey:  string = params.request.groupKeys[params.request.groupKeys.length - 1];
    let parentType: string = '';
    let parentData:    any = null;

    if (typeof params.parentNode.data != 'undefined') {

      parentData = params.parentNode.data;
      parentType = parentData.type;

      if (parentData.id == parentKey) {

        Logger.debug(`[datasource] expanding ${parentKey} [${parentType}]...`);
      }
    }

    if (params.request.groupKeys.length == 0) {

      Logger.debug(`[datasource] fetching (root) simulation node...`);

      ulds.userLogs.root(ulds.logContext)
        .pipe(first())
        .subscribe(
          (rootNode: any) => {

            /* build a node from server side data */

            const root: any =  UserLogNodeFactory.build(rootNode, {
              index:       0,
              isExpanded:  true,
              hasChildren: true
            });

            root["context"] = ulds.context;

            /* pass back to AG Grid */

            // @ts-ignore
            params.success({ rowData: [root], rowCount: 1 });

          },
          (error: any) => {

            Logger.error(`[datasource] problems finding root in ${ulds.logContext}: `, error);

            // @ts-ignore
            params.fail();
          });

      return ;
    }

    /* do we need the top level children (the trains)? */

    if (parentType == "simulation") {

      Logger.debug(`[datasource] fetching (trains) top level children...`);

      ulds.userLogs.trains(ulds.logContext)
        .pipe(first())
        .subscribe(
          (trainNodes: any[]) => {

            let trains: any[] = [];
            const iid: string = (ulds.train != null) ? Train.iid(ulds.train) : '';

            /* decode the trains ... */

            trainNodes.forEach((train: any, index: number) => {

              /* don't keep trains that don't actually have any dispatches ('dc' is the dispatch count) */

              if (train.dc <= 0) {

                return;
              }

              /* use our node factory to build the train nodes */

              const tNode: any = UserLogNodeFactory.build(train, {
                index,
                isExpanded: false
              });

              tNode["context"] = ulds.context;

              /* if this train is part of the initial focus of the tree (i.e. auto-expanded) mark it as such */

              if (iid == tNode.id) {

                tNode['initialfocus'] = true;
                tNode.isExpanded      = true;
              }

              trains.push(tNode);

            });

            /* make sure trains are ordered */

            trains.sort((a:any, b:any) => {

              return a.seq - b.seq;
            });

            /* pass back to AG Grid */

            this.returnSlice(params, trains);
          },
          (error: any) => {

            Logger.error(`[datasource] problems finding trains in ${ulds.logContext}: `, error);

            // @ts-ignore
            params.fail();

          });

      return ;
    }

    /*
     * some nodes are special in that they create their own children when they are built with the
     * data we get in the node itself.  So when we are trying to expand one of those nodes, we can't
     * just ask the server for the children...there isn't any such thing for those nodes, the
     * children were constructed already and are waiting in that nodes 'children' field.
     *
     */

    const constructedChildren: string[] = [
      'accepted-authorities',
      'rejected-authorities',
      'selection',
      'accepted-moves',
      'rejected-moves',
      'possible-moves'
    ];

    if (constructedChildren.includes(parentType)) {

      /* pass back to AG Grid */

      setTimeout( () => {

        /* the children were already constructed, just use the 'children' field. */

        let leaves: any[] = [];

        parentData.children.forEach((child: any, index: number) => {

          /* use our node factory to build the train nodes */

          let leaf: any = UserLogNodeFactory.build(child, {
            index,
            isExpanded: false,
            parent: parentData
          });

          if (leaf === null) {

            return;
          }

          leaf["context"] = ulds.context;

          /* only keep the valid children */

          leaves.push(leaf);
        });

        leaves.sort((a: any, b: any) => {

          return a.seq - b.seq;
        });

        ulds.returnSlice(params, leaves);

      }, 50);

      return ;
    }

    /* general case, just get the children of whatever this parent is. */

    ulds.userLogs.childrenPromise(ulds.logContext, parentKey).then(
      (children: any) => {

        let nodes: any[] = [];

        /* use our node factory to convert the MongoDB entries into nodes for this tree */

        children.forEach((child: any, index: number) => {

          /* use our node factory to build the train nodes */

          let leaf: any = UserLogNodeFactory.build(child, {
            index,
            isExpanded: false,
            parent: parentData
          });

          if (leaf === null) {

            return;
          }

          leaf["context"] = ulds.context;

          /* only keep the valid children */

          nodes.push(leaf);
        });

        /* make sure nodes are ordered sequentially, with newest at the bottom */

        nodes.sort((a: any, b: any) => {

          return a.seq - b.seq;
        });

        /*
         * build up the "actuals", for train nodes, some of the children would have been departure
         * event contexts, which note the commit deployment, the dispatch and dispatch group that were
         * involved in an actual move of the train, rather than only being attempted, but then failing
         * to move for various reasons.
         *
         * this children updated the actualMoves[] field in the train parent, so now, if we just
         * processed a train parent's children, we can post-process the children by looking up
         * the dispatch groups, and using actualMoves[] we we can mark which ones were the actual dispatches.
         *
         */

        if (parentType == 'train') {

          /*
           * for each actual, try to find the dispatch group child, and give it a pointer to
           * the dispatch event, when we render the nodes, we will show the ones with a departure
           * event as being the "actual" as opposed to only attempted.
           *
           */

          let dpName: string = (ulds.dp != null) ? ulds.dp._name.trim().toUpperCase() : '';

          parentData.actualMoves.forEach((actual: any, i: number) => {

            /* try to find the decision point group context that this departure event was in */

            let match: any = nodes.find(x => ((x.type == 'decision-point-group') && (x.id == actual.tg)));

            if (!match) {

              return;
            }

            /* give it a pointer to the departure */

            const run: number = actual.r.toFixed(4);
            const h: string = actual.h;

            match.depart = actual;
            match.name = `${match.dp} ${match.mileage} mi, ARR: ${actual.ar} => DPT: ${actual.d}, HDY: ${h}, RUN: ${run}`;

            /*
             * check for leafs tha are part of the branch in focus; i.e. if we came from
             * the string line viewer, we are going to be trying to auto-open to a given
             * train and decision point.
             *
             */

            if (match.dp.trim().toUpperCase() == dpName) {

              /*
               * this is the decision point, but is this particular time point the one
               * where it actually moved/was dispatched?
               *
               */

              /* this is the one we want! */

              match['initialfocus'] = true;
              match.isExpanded = true;

            }

          });

        }

        if (parentType == 'decision-point-group') {

          parentData.parent.actualMoves.forEach((actual: any, i: number) => {

            /* try to find the dispatch that this departure event was in */

            let match: any = nodes.find(x => ((x.type == 'dispatch') && (x.id == actual.td)));

            if (!match) {

              return;
            }

            match['initialfocus'] = true;
            match.isExpanded = true;

          });

        }

        if (parentType == 'dispatch') {

          /* try to find the deployment that this departure event was in */

          parentData.parent.parent.actualMoves.forEach((actual: any, i: number) => {

            let match: any = nodes.find(x => ((x.type == 'deployment') && (x.id == actual.tc)));

            if (!match) {

              return;
            }

            match['initialfocus'] = true;
            match.isExpanded      = true;

          });
        }

        /* pass back to AG Grid */

        this.returnSlice(params, nodes);
      },
      (error: any) => {

        Logger.error(`[datasource] problems finding children in ${ulds.logContext} for '${parentKey}': `, error);

        // @ts-ignore
        params.fail();
      });

  }

  public returnSlice(params: IServerSideGetRowsParams, rows: any[]): void {

    const start: number = params.request.startRow;
    const end:   number = params.request.endRow;
    let   data:   any[] = rows.slice(start, end);

    if (start != 0) {

      Logger.debug(`[datasource] partial render: start=${start}, end=${end}`, rows);
    }

    // @ts-ignore
    params.success({ rowData: data, rowCount: rows.length });
  }

  /** do any cleanup */
  public destroy() {

    /* we don't have any state to clear */
  }
}
